/* Sika Design 3D generator: download packshot-style images of the configuration.
   Added 18 Oct 2026. Loaded by sections/custom-product-generator.liquid.
   Views follow the packshot set: Side, Plain side, Front, Back, Back side.
   Each PNG has a transparent background, the ground shadow, the model and
   every material name (top left) and a small Sika Design logo (bottom left).
   Roll back: remove the two cpg-snapshots lines before {% schema %} in the section. */
(function () {
  'use strict';

  var VIEWS = [
    { key: 'Side',       label: 'Side',       theta: -35 },
    { key: 'Plain_side', label: 'Plain side', theta: -90 },
    { key: 'Front',      label: 'Front',      theta: 0 },
    { key: 'Back',       label: 'Back',       theta: 180 },
    { key: 'Back_side',  label: 'Back side',  theta: -145 }
  ];
  var PHI = 80;      // camera elevation (deg from straight down), as the packshots
  var FOV = 12;      // long lens, flat studio perspective
  var FRAC = 0.56;   // chair height as a share of the image height
  var YOFF = 0.03;   // chair sits slightly low in the frame, as the packshots
  var LOGO = '/cdn/shop/files/logo_1.png?width=800';
  var T = {
    tab: 'Download images',
    title: 'Download images',
    intro: 'Five views of your configuration, with the model and all materials written on each image. Send them to us to request this configuration, or to your client as visual references.',
    preparing: 'Preparing your images',
    ready: 'Your images are ready. Tap an image to download it on its own.',
    zip: 'Download all images',
    share: 'Save to photos',
    failed: 'Sorry, the images could not be made. Please try again.',
    close: 'Close'
  };

  /* ---------- helpers ---------- */
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function raf() { return new Promise(function (r) { requestAnimationFrame(function () { r(); }); }); }
  function txt(el) { return el ? el.textContent.replace(/\s+/g, ' ').trim() : ''; }
  function nice(s) { return (s && s === s.toUpperCase()) ? s.charAt(0) + s.slice(1).toLowerCase() : s; }
  function slug(s) { return (s || '').replace(/®/g, '').replace(/[|\/]+/g, ' ').trim().replace(/[^A-Za-z0-9æøåÆØÅäöüÄÖÜ]+/g, '_').replace(/^_+|_+$/g, ''); }
  function loadImg(src) {
    return new Promise(function (res, rej) { var i = new Image(); i.crossOrigin = 'anonymous'; i.onload = function () { res(i); }; i.onerror = rej; i.src = src; });
  }
  function isPhone() { return (window.matchMedia && matchMedia('(pointer: coarse)').matches) || window.innerWidth < 768; }

  /* ---------- what the customer chose ---------- */
  function fieldValue(el) {
    var cur = el && el.querySelector('.cpg__dd-current');
    if (!cur) return '';
    var vn = cur.querySelector('.cpg__val-name');
    if (vn) {
      var cols = txt(cur.querySelector('.cpg__val-cols'));
      return txt(vn) + (cols ? ' – ' + cols : '');
    }
    var name = '';
    for (var i = 0; i < cur.children.length; i++) {
      var c = cur.children[i];
      if (c.tagName === 'SPAN' && !c.classList.contains('cpg__code') && !c.classList.contains('cpg__swatch')) { name = txt(c); break; }
    }
    if (!name) name = txt(cur);
    var code = txt(cur.querySelector('.cpg__code'));
    return name + (code ? ' (' + code + ')' : '');
  }

  function readSpec(root) {
    var f = function (k) { return root.querySelector('[data-cpg-field="' + k + '"]'); };
    var model = fieldValue(f('product')) || txt(root.querySelector('.cpg__stagehead h1, .cpg__stagehead h2'));
    var coll = txt(root.querySelector('.cpg__collection'));
    var grouped = root.classList.contains('cpg--grouped');
    var mixed = root.classList.contains('cpg--mixed');
    var items = [], groupName = '';
    if (grouped) {
      groupName = fieldValue(f('frame'));
      if (!mixed && groupName) items.push(['Material', groupName]);
      if (mixed || !groupName) [['mixframe', 'Frame'], ['mixpipe', 'Pipe'], ['mixweave', 'Weave & bindings']].forEach(function (p) {
        var v = fieldValue(f(p[0]));
        if (v) items.push([p[1], v]);
      });
    } else {
      ['frame', 'pipe', 'bindings', 'seat', 'back'].forEach(function (k) {
        var el = f(k);
        if (!el || getComputedStyle(el).display === 'none') return;
        var v = fieldValue(el);
        if (v) items.push([nice(txt(el.querySelector('.cpg__label'))) || k, v]);
      });
    }
    var cname = txt(root.querySelector('[data-cpg-cushion-name]'));
    if (cname && !/^no\b/i.test(cname)) {
      var csub = txt(root.querySelector('[data-cpg-cushion-sub]'));
      items.push(['Seat cushion', cname + (csub && csub.length <= 14 && csub.indexOf(cname) < 0 ? ' (' + csub + ')' : '')]);
    }
    var bblk = root.querySelector('[data-cpg-bcushion]'), bname = bblk && !bblk.hidden ? txt(bblk.querySelector('[data-cpg-cushion-name]')) : '';
    if (bname && !/^no\b/i.test(bname)) items.push(['Back cushion', bname]);
    var mat = (grouped && !mixed && groupName) ? groupName.split('|')[0] : 'Custom';
    return { model: model, coll: coll, items: items, mat: mat };
  }

  /* ---------- render the five views from the live viewer ---------- */
  async function renderViews(mv, S, onStep) {
    if (!mv.loaded) await new Promise(function (r) { mv.addEventListener('load', r, { once: true }); });
    var keys = ['camera-orbit', 'camera-target', 'field-of-view', 'min-camera-orbit', 'max-camera-orbit', 'min-field-of-view', 'max-field-of-view'];
    var saved = {};
    keys.forEach(function (k) { saved[k] = mv.getAttribute(k); });
    var autoRot = mv.hasAttribute('auto-rotate');
    var style = mv.getAttribute('style');
    var css = Math.round(S / (window.devicePixelRatio || 1));
    var out = [];
    try {
      mv.removeAttribute('auto-rotate');
      if (mv.resetTurntableRotation) mv.resetTurntableRotation(0);
      [['position', 'fixed'], ['left', '0'], ['top', '0'], ['width', css + 'px'], ['height', css + 'px'],
       ['max-width', 'none'], ['max-height', 'none'], ['min-height', '0'], ['z-index', '2147482000'], ['transform', 'none']]
        .forEach(function (p) { mv.style.setProperty(p[0], p[1], 'important'); });
      await wait(300);
      var d = mv.getDimensions(), c = mv.getBoundingBoxCenter();
      var H = d.y / FRAC;
      var dist = (H / 2) / Math.tan(FOV * Math.PI / 360);
      mv.setAttribute('min-field-of-view', '1deg');
      mv.setAttribute('max-field-of-view', '90deg');
      mv.setAttribute('min-camera-orbit', 'auto auto 0m');
      mv.setAttribute('max-camera-orbit', 'auto auto 100m');
      mv.setAttribute('camera-target', c.x + 'm ' + (c.y + YOFF * H) + 'm ' + c.z + 'm');
      mv.setAttribute('field-of-view', FOV + 'deg');
      for (var i = 0; i < VIEWS.length; i++) {
        onStep && onStep(i, VIEWS[i]);
        mv.setAttribute('camera-orbit', VIEWS[i].theta + 'deg ' + PHI + 'deg ' + dist + 'm');
        if (mv.updateComplete) await mv.updateComplete;
        await raf();
        mv.jumpCameraToGoal();
        await raf(); await raf();
        await wait(350);
        out.push(await mv.toBlob({ mimeType: 'image/png', idealAspect: false }));
      }
    } finally {
      keys.forEach(function (k) { if (saved[k] == null) mv.removeAttribute(k); else mv.setAttribute(k, saved[k]); });
      if (style == null) mv.removeAttribute('style'); else mv.setAttribute('style', style);
      if (autoRot) mv.setAttribute('auto-rotate', '');
      if (mv.updateComplete) await mv.updateComplete;
      await raf();
      mv.jumpCameraToGoal();
    }
    return out;
  }

  /* ---------- text, logo and final PNG ---------- */
  function wrapLines(x, s, maxW) {
    var words = s.split(' '), lines = [], line = '';
    words.forEach(function (w) {
      var t = line ? line + ' ' + w : w;
      if (x.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t;
    });
    if (line) lines.push(line);
    return lines;
  }

  async function compose(blob, S, sp, view, logo) {
    var url = URL.createObjectURL(blob);
    var img = await loadImg(url);
    URL.revokeObjectURL(url);
    var cv = document.createElement('canvas');
    cv.width = cv.height = S;
    var x = cv.getContext('2d');
    x.drawImage(img, 0, 0, S, S);

    var m = Math.round(S * 0.045), ink = '#565657', soft = '#8a8580';
    var serif = 'Cormorant, "Cormorant Garamond", Georgia, serif', sans = 'Lato, "Helvetica Neue", Arial, sans-serif';
    function put(s, size, weight, fam, color, px, py, align, ls) {
      x.font = weight + ' ' + Math.round(size) + 'px ' + fam;
      x.textAlign = align || 'left';
      x.textBaseline = 'alphabetic';
      if ('letterSpacing' in x) x.letterSpacing = (ls || 0) + 'px';
      x.lineJoin = 'round';
      x.strokeStyle = 'rgba(255,255,255,0.8)';
      x.lineWidth = Math.max(2, size * 0.2);
      x.strokeText(s, px, py);
      x.fillStyle = color;
      x.fillText(s, px, py);
    }

    var y = m + S * 0.032;
    put(sp.model, S * 0.036, 600, serif, ink, m, y);
    y += S * 0.03;
    put([sp.coll, view.label].filter(Boolean).join('  ·  ').toUpperCase(), S * 0.0125, 600, sans, soft, m, y, 'left', S * 0.0013);
    y += S * 0.062;

    var maxW = S * 0.24, vs = S * 0.0165;
    sp.items.forEach(function (it) {
      put(it[0].toUpperCase(), S * 0.0115, 700, sans, soft, m, y, 'left', S * 0.0011);
      y += S * 0.024;
      x.font = '400 ' + Math.round(vs) + 'px ' + sans;
      if ('letterSpacing' in x) x.letterSpacing = '0px';
      wrapLines(x, it[1], maxW).forEach(function (line) { put(line, vs, 400, sans, ink, m, y); y += S * 0.022; });
      y += S * 0.016;
    });

    if (logo) {
      var lw = S * 0.12, lh = lw * logo.naturalHeight / logo.naturalWidth;
      x.globalAlpha = 0.6;
      x.drawImage(logo, m, S - m - lh, lw, lh);
      x.globalAlpha = 1;
    }
    var dt = new Date();
    var ds = ('0' + dt.getDate()).slice(-2) + '.' + ('0' + (dt.getMonth() + 1)).slice(-2) + '.' + dt.getFullYear();
    put('sika-design.com  ·  ' + ds, S * 0.011, 400, sans, soft, S - m, S - m, 'right', S * 0.0006);

    return new Promise(function (r) { cv.toBlob(r, 'image/png'); });
  }

  /* ---------- tiny ZIP (stored, PNGs are already compressed) ---------- */
  var CRC = (function () {
    var t = new Uint32Array(256);
    for (var n = 0; n < 256; n++) { var c = n; for (var k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
    return t;
  })();
  function crc32(u) { var c = 0xFFFFFFFF; for (var i = 0; i < u.length; i++) c = CRC[(c ^ u[i]) & 255] ^ (c >>> 8); return (c ^ 0xFFFFFFFF) >>> 0; }
  function makeZip(files) {
    var enc = new TextEncoder(), parts = [], central = [], off = 0, now = new Date();
    var tm = (now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1);
    var dm = ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();
    files.forEach(function (f) {
      var nm = enc.encode(f.name), crc = crc32(f.data), sz = f.data.length;
      var h = new DataView(new ArrayBuffer(30));
      h.setUint32(0, 0x04034b50, true); h.setUint16(4, 20, true); h.setUint16(6, 0x0800, true); h.setUint16(8, 0, true);
      h.setUint16(10, tm, true); h.setUint16(12, dm, true); h.setUint32(14, crc, true); h.setUint32(18, sz, true);
      h.setUint32(22, sz, true); h.setUint16(26, nm.length, true); h.setUint16(28, 0, true);
      parts.push(h.buffer, nm, f.data);
      var c = new DataView(new ArrayBuffer(46));
      c.setUint32(0, 0x02014b50, true); c.setUint16(4, 20, true); c.setUint16(6, 20, true); c.setUint16(8, 0x0800, true);
      c.setUint16(10, 0, true); c.setUint16(12, tm, true); c.setUint16(14, dm, true); c.setUint32(16, crc, true);
      c.setUint32(20, sz, true); c.setUint32(24, sz, true); c.setUint16(28, nm.length, true);
      c.setUint32(42, off, true);
      central.push(c.buffer, nm);
      off += 30 + nm.length + sz;
    });
    var cs = 0; central.forEach(function (b) { cs += b.byteLength; });
    var e = new DataView(new ArrayBuffer(22));
    e.setUint32(0, 0x06054b50, true); e.setUint16(8, files.length, true); e.setUint16(10, files.length, true);
    e.setUint32(12, cs, true); e.setUint32(16, off, true);
    return new Blob(parts.concat(central, [e.buffer]), { type: 'application/zip' });
  }

  function saveBlob(blob, name) {
    var a = document.createElement('a');
    var u = URL.createObjectURL(blob);
    a.href = u; a.download = name; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(function () { URL.revokeObjectURL(u); }, 60000);
  }

  /* ---------- UI ---------- */
  var ICON = '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v9M4.2 6.9 8 10.7l3.8-3.8M2 13.5h12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function buildDialog() {
    var d = document.createElement('div');
    d.className = 'cpg-snap';
    d.hidden = true;
    d.setAttribute('role', 'dialog');
    d.setAttribute('aria-modal', 'true');
    d.setAttribute('aria-label', T.title);
    d.innerHTML =
      '<div class="cpg-snap__card">' +
        '<button type="button" class="cpg-snap__close" aria-label="' + T.close + '">' +
          '<svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 2l12 12M14 2 2 14" stroke="currentColor" stroke-width="1.3"/></svg></button>' +
        '<h3 class="cpg-snap__title">' + T.title + '</h3>' +
        '<p class="cpg-snap__intro">' + T.intro + '</p>' +
        '<div class="cpg-snap__bar"><span></span></div>' +
        '<p class="cpg-snap__status" aria-live="polite"></p>' +
        '<div class="cpg-snap__grid"></div>' +
        '<div class="cpg-snap__actions">' +
          '<button type="button" class="button w-full cpg-snap__zip" disabled>' + T.zip + '</button>' +
          '<button type="button" class="button w-full cpg-snap__share" hidden>' + T.share + '</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(d);
    return d;
  }

  function setup(root) {
    if (root.__cpgSnap) return;
    var mv = root.querySelector('model-viewer');
    if (!mv) return;
    root.__cpgSnap = true;

    var tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'cpg-snap__tab';
    tab.innerHTML = ICON + '<span>' + T.tab + '</span>';
    /* the tab lives in the grid (model-viewer clips its children) and runs out to the screen edge, like the landing page pills */
    var host = mv.closest('.cpg__grid') || mv.parentElement;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(tab);
    /* desktop: left edge of the 3D view. Phone layout: right edge, level with the
       "Selected materials" heading (Dee, 18 Oct 22:57) */
    var DESK = '(min-width: 1200px), (min-width: 768px) and (orientation: landscape)';
    function placeTab() {
      var hr = host.getBoundingClientRect(), mr = mv.getBoundingClientRect();
      /* phone: level with the Material row (Dee, 23:03); heading as fallback */
      var mat = root.querySelector('[data-cpg-field="frame"]');
      /* Alu Affäire (19 Oct 2026): the part rows are hidden there - level with the Materials heading instead */
      var amat = root.querySelector('.cpg-amob__label');
      if (amat && amat.offsetParent && amat.offsetHeight) mat = amat;
      var head = (mat && mat.offsetParent && mat.offsetHeight) ? mat : root.querySelector('.cpg__data-head');
      var right = !matchMedia(DESK).matches && head && head.offsetParent;
      tab.classList.toggle('is-right', !!right);
      if (right) {
        var r = head.getBoundingClientRect(), vw = document.documentElement.clientWidth;
        tab.style.paddingLeft = '';
        tab.style.left = 'auto';
        tab.style.right = -Math.max(0, Math.round(vw - hr.right)) + 'px';
        tab.style.top = Math.round(r.top - hr.top + r.height / 2 - tab.offsetHeight / 2) + 'px';
        /* 20 Oct 2026 (Dee): the pill's bottom edge on the bottom of the model name (Share sits above, level with "Selected materials") */
        var nm = root.querySelector('[data-cpg-field="product"] .cpg__dd-current span');
        if (nm && nm.offsetParent) {
          /* long names wrap before the pill instead of running under it */
          var box = nm.closest('.cpg__dd-current') || nm.parentElement; box.style.paddingRight = '';
          var tl = tab.getBoundingClientRect().left, br = box.getBoundingClientRect().right, rgw = document.createRange(); rgw.selectNodeContents(nm);
          if (rgw.getBoundingClientRect().right > tl - 10) { box.style.display = 'block'; box.style.paddingRight = Math.max(0, Math.round(br - tl + 12)) + 'px'; }
          var rg = document.createRange(); rg.selectNodeContents(nm); var nrs = rg.getClientRects(), nb = nrs.length ? nrs[nrs.length - 1] : rg.getBoundingClientRect();
          var nfs = parseFloat(getComputedStyle(nm).fontSize) || 16, inkBot = nb.bottom - (nb.height - nfs) / 2 - nfs * 0.02;
          tab.style.top = Math.round(inkBot - hr.top - tab.offsetHeight) + 'px';
        }
      } else {
        /* desktop: bottom of the 3D view, icon on the page grid line, pill runs out to the screen edge (Dee, 23:25) */
        var gl = hr.left > 0 ? Math.round(hr.left) : 0;
        tab.style.right = 'auto';
        tab.style.left = -gl + 'px';
        tab.style.paddingLeft = gl ? gl + 'px' : '';
        tab.style.top = Math.round(mr.bottom - hr.top - tab.offsetHeight - 24) + 'px';
      }
    }
    if (window.ResizeObserver) new ResizeObserver(placeTab).observe(host);
    placeTab();
    window.addEventListener('resize', placeTab);
    mv.addEventListener('load', placeTab);

    var dlg = null, busy = false, files = [], urls = [];

    function close() {
      if (busy || !dlg) return;
      dlg.hidden = true;
      document.documentElement.classList.remove('cpg-snap-open');
      urls.forEach(function (u) { URL.revokeObjectURL(u); });
      urls = []; files = [];
    }

    async function run() {
      if (busy) return;
      if (!dlg) {
        dlg = buildDialog();
        dlg.addEventListener('click', function (e) { if (e.target === dlg) close(); });
        dlg.querySelector('.cpg-snap__close').addEventListener('click', close);
        document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !dlg.hidden) close(); });
        dlg.querySelector('.cpg-snap__zip').addEventListener('click', function () {
          if (!files.length) return;
          saveBlob(makeZip(files.map(function (f) { return { name: f.name, data: f.data }; })), files.zipName);
        });
        dlg.querySelector('.cpg-snap__share').addEventListener('click', function () {
          var fl = files.map(function (f) { return new File([f.blob], f.name, { type: 'image/png' }); });
          navigator.share({ files: fl, title: files.title }).catch(function () {});
        });
      }
      var bar = dlg.querySelector('.cpg-snap__bar span'), status = dlg.querySelector('.cpg-snap__status');
      var grid = dlg.querySelector('.cpg-snap__grid'), zipBtn = dlg.querySelector('.cpg-snap__zip'), shareBtn = dlg.querySelector('.cpg-snap__share');
      grid.innerHTML = ''; zipBtn.disabled = true; shareBtn.hidden = true;
      dlg.classList.remove('is-ready');
      bar.style.width = '0%';
      status.textContent = T.preparing + '…';
      dlg.hidden = false;
      document.documentElement.classList.add('cpg-snap-open');
      busy = true;
      try {
        var S = isPhone() ? 1500 : 2000;
        var sp = readSpec(root);
        var logo = null;
        try { logo = await loadImg(LOGO); } catch (e) {}
        try {
          await Promise.all([document.fonts.load('600 40px Cormorant'), document.fonts.load('400 20px Lato'), document.fonts.load('700 20px Lato')]);
        } catch (e) {}
        var raw = await renderViews(mv, S, function (i, v) {
          status.textContent = T.preparing + ' – ' + v.label + ' (' + (i + 1) + '/' + VIEWS.length + ')';
          bar.style.width = (i / VIEWS.length * 80) + '%';
        });
        var base = slug(sp.model) + '_' + slug(sp.mat);
        files = [];
        files.zipName = base + '_images.zip';
        files.title = sp.model;
        for (var i = 0; i < raw.length; i++) {
          var b = await compose(raw[i], S, sp, VIEWS[i], logo);
          var data = new Uint8Array(await b.arrayBuffer());
          files.push({ name: base + '_' + (i + 1) + '_' + VIEWS[i].key + '.png', blob: b, data: data });
          bar.style.width = (80 + (i + 1) / raw.length * 20) + '%';
        }
        files.forEach(function (f, i) {
          var u = URL.createObjectURL(f.blob); urls.push(u);
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cpg-snap__thumb';
          btn.innerHTML = '<img alt="" src="' + u + '"><span>' + VIEWS[i].label + '</span>';
          btn.addEventListener('click', function () { saveBlob(f.blob, f.name); });
          grid.appendChild(btn);
        });
        zipBtn.disabled = false;
        try {
          var test = new File([files[0].blob], files[0].name, { type: 'image/png' });
          if (isPhone() && navigator.canShare && navigator.canShare({ files: [test] })) shareBtn.hidden = false;
        } catch (e) {}
        status.textContent = T.ready;
        dlg.classList.add('is-ready');
      } catch (err) {
        console.error('cpg-snapshots', err);
        status.textContent = T.failed;
      } finally {
        busy = false;
      }
    }
    tab.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); run(); });
  }

  function init() { document.querySelectorAll('.cpg').forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  window.addEventListener('load', init);
})();
